import Skeleton from "@mui/material/Skeleton";
import styled from "styled-components";

import theme from "../../../styles/theme";
import { isDark } from "../../../utils/helpers/colors";
import { Ellipsis, positionColor, skeletonSx, skeletonWidth } from "../styles";
import { ChampionPhoto, MoreChip, TitleChip, titleWord } from "./ChampionsShared";

// Row treatment lifted from components/SeasonTable — the soft 70%→100% team-colour fade that
// sweeps across on hover, alternating row bases, and the squared race-cell badges.
const getOppositeColor = (color, defaultColor) => {
  if (!color) color = defaultColor;
  return isDark(color) ? theme.colors.white : theme.colors.darkLight;
};

// The podium tapers: 1st is the biggest, 2nd a step down, 3rd another step, everyone else shares
// the base row height so the table keeps its rhythm.
// photoW/photoH keep the 140:150 ratio used for driver photos on the drivers page
// (components/Driver/DriverCard.jsx) so champion portraits crop the same way everywhere.
const SIZES = [
  { rank: 28, photoW: 84, photoH: 90, name: 21, pad: 16, count: 24 },
  { rank: 24, photoW: 72, photoH: 77, name: 19, pad: 13, count: 22 },
  { rank: 21, photoW: 62, photoH: 66, name: 17, pad: 11, count: 20 },
  { rank: 18, photoW: 52, photoH: 56, name: 15, pad: 9, count: 18 },
];
const sizeOf = (index) => SIZES[Math.min(index, SIZES.length - 1)];

// A champion with a long career would otherwise wrap his badges onto extra lines and blow the row
// height out — past this many the rest collapse into a single "+N" badge.
const MAX_BADGES = 4;

const Table = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColPosition = styled.div`
  font-family: "Roboto Condensed", sans-serif;
  font-size: ${({ $size }) => $size.rank}px;
  font-weight: 700;
  text-align: center;
  color: ${({ $rank }) => positionColor($rank)};
`;

const DriverName = styled(Ellipsis)`
  font-weight: ${({ $size }) => ($size.name > 15 ? 700 : 600)};
  font-size: ${({ $size }) => $size.name}px;
`;

const TeamName = styled(Ellipsis)`
  color: ${({ color }) => color};
  font-weight: 600;
  font-size: 13px;
  opacity: 0.8;
`;

const Row = styled.div`
  display: grid;
  gap: 14px;
  align-items: center;
  padding: ${({ $size }) => $size.pad}px 12px;
  cursor: pointer;

  background: linear-gradient(
      to left,
      ${theme.colors.dark} 70%,
      ${({ color }) => color || theme.colors.white} 100%
    )
    right;
  background-size: 200% 150%;
  background-position: right;

  &:nth-of-type(2n + 1) {
    background: linear-gradient(
        to left,
        ${theme.colors.darkLight} 70%,
        ${({ color }) => color || theme.colors.white} 100%
      )
      right;
    background-size: 200% 150%;
    background-position: right;
  }

  transition: all 0.3s ease-in-out;
  user-select: none;

  grid-template-columns: ${({ $size }) => `${$size.rank + 10}px ${$size.photoW}px`} 1fr auto 76px;

  @media screen and (max-width: ${theme.breakpointsCustom.md}px) {
    grid-template-columns: ${({ $size }) => `${$size.rank + 10}px ${$size.photoW}px`} 1fr 76px;
  }

  &:hover {
    background-position: left;

    & ${ColPosition}, & ${DriverName}, & ${TeamName} {
      color: ${({ color }) => getOppositeColor(color, theme.colors.white)};
      transition: all 0.3s ease-in-out;
    }
  }
`;

const Photo = styled(ChampionPhoto)`
  display: block;
  width: ${({ $size }) => $size.photoW}px;
  height: ${({ $size }) => $size.photoH}px;
  object-fit: cover;
  object-position: top center;
  background: ${theme.colors.dark};
  border-radius: 2px;
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ColSeasons = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  margin-left: auto;

  @media screen and (max-width: ${theme.breakpointsCustom.md}px) {
    display: none;
  }
`;

const ColTitles = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 5px;
`;

const TitlesValue = styled.span`
  font-family: "Roboto Condensed", sans-serif;
  font-size: ${({ $size }) => $size.count}px;
  font-weight: 700;
  line-height: 1;
  color: ${theme.colors.gold};
`;

const TitlesLabel = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.grey};

  @media screen and (max-width: ${theme.breakpointsCustom.lg}px) {
    display: none;
  }
`;

export function ChampionsTable({ champions, onSelectDriver }) {
  return (
    <Table>
      {champions.map((champion, index) => {
        const size = sizeOf(index);
        const shown = champion.titles.slice(0, MAX_BADGES);
        const rest = champion.titles.slice(MAX_BADGES);

        return (
          <Row
            key={champion.driverId}
            color={champion.accent}
            $size={size}
            onClick={() => onSelectDriver(champion.driverId)}
          >
            <ColPosition $rank={index + 1} $size={size}>
              {index + 1}
            </ColPosition>
            <Photo $size={size} src={champion.driverImage} alt={champion.driverName} />
            <Identity>
              <DriverName $size={size}>{champion.driverName}</DriverName>
              <TeamName color={champion.accent}>{champion.latestTeam?.name}</TeamName>
            </Identity>
            <ColSeasons>
              {shown.map((title) => (
                <TitleChip key={title.seasonId} title={title} />
              ))}
              {rest.length > 0 && <MoreChip titles={rest} />}
            </ColSeasons>
            <ColTitles>
              <TitlesValue $size={size}>×{champion.titleCount}</TitlesValue>
              <TitlesLabel>{titleWord(champion.titleCount)}</TitlesLabel>
            </ColTitles>
          </Row>
        );
      })}
    </Table>
  );
}

// Five rows: sizeOf() caps at SIZES[3], so the top three taper exactly like the loaded podium and
// the last two sit at the base height. Everything below reuses the real Row/Photo/Identity
// styled components above, so the skeleton and the loaded table cannot drift apart.
const SKELETON_ROWS = 5;
const SKELETON_BADGES = 3;

export function ChampionsTableSkeleton() {
  return (
    <Table>
      {Array.from({ length: SKELETON_ROWS }, (_, index) => {
        const size = sizeOf(index);

        return (
          <Row key={index} $size={size} style={{ cursor: "default" }}>
            {/* The rank is not backend data, and its gold/silver/bronze tint is what makes the
                podium taper legible while the rest is still loading. */}
            <ColPosition $rank={index + 1} $size={size}>
              {index + 1}
            </ColPosition>
            <Skeleton variant="rectangular" width={size.photoW} height={size.photoH} sx={skeletonSx} />
            <Identity>
              <Skeleton
                variant="text"
                width={skeletonWidth(index)}
                height={size.name + 4}
                sx={skeletonSx}
              />
              <Skeleton variant="text" width={skeletonWidth(index + 4)} height={15} sx={skeletonSx} />
            </Identity>
            <ColSeasons>
              {Array.from({ length: SKELETON_BADGES }, (_, badge) => (
                <Skeleton key={badge} variant="rectangular" width={34} height={20} sx={skeletonSx} />
              ))}
            </ColSeasons>
            <ColTitles>
              <Skeleton variant="text" width={40} height={size.count} sx={skeletonSx} />
            </ColTitles>
          </Row>
        );
      })}
    </Table>
  );
}
